// DisasterMessagesPage.js 수정사항

import React, { useState, useEffect } from 'react';
import DisasterMessagesSearch from '../components/disasterMessages/DisasterMessageSearch';
import DisasterMessagesList from '../components/disasterMessages/DisasterMessagesList';
import disasterMessagesApi from '../services/disasterMessagesApi';
import './DisasterMessagesPage.css';

const DisasterMessagesPage = () => {
  // 상태 관리
  const [disasterMessages, setDisasterMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // 🔥 새로 추가: 캐시 상태 관리
  const [cacheInfo, setCacheInfo] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);

  // 오늘 날짜를 YYYYMMDD 형식으로 얻기
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // 검색 파라미터 - 오늘 날짜로 초기화
  const [searchParams, setSearchParams] = useState({
    pageNo: 1,
    numOfRows: 20,
    crtDt: getTodayDate(),
    rgnNm: '',
    filters: {
      keyword: '',
      region: '',
      type: '',
      agency: '',
      missingPersonOnly: false
    }
  });

  // 🔥 수정된 재난문자 데이터 가져오기 함수
  const fetchDisasterMessages = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      console.log('재난문자 검색 파라미터:', searchParams, 'forceRefresh:', forceRefresh);

      // 🔥 변경: 저장소 기반 API 요청 파라미터 준비
      const apiParams = {
        pageNo: searchParams.pageNo,
        numOfRows: searchParams.numOfRows,
        returnType: 'json',
        // 🔥 날짜 파라미터 추가 (중요!)
        crtDt: searchParams.crtDt || '',  // 날짜 필터
        // 🔥 클라이언트 필터 파라미터 추가
        keyword: searchParams.filters.keyword,
        region: searchParams.filters.region || searchParams.rgnNm,
        type: searchParams.filters.type,
        agency: searchParams.filters.agency,
        missingPersonOnly: searchParams.filters.missingPersonOnly
      };

      // 🔥 날짜 파라미터 로깅 추가
      if (searchParams.crtDt) {
        console.log(`📅 날짜 필터 적용: ${searchParams.crtDt}`);
      }

      // 선택적 파라미터 추가 (기존 코드 제거 - 중복됨)
      // if (searchParams.crtDt) {
      //   apiParams.crtDt = searchParams.crtDt;  // 이미 위에서 추가함
      // }

      if (searchParams.rgnNm && !apiParams.region) {
        apiParams.rgnNm = searchParams.rgnNm;
      }

      // 🔥 강제 새로고침 시에는 캐시 갱신 API 먼저 호출
      if (forceRefresh) {
        try {
          console.log('🔄 강제 새로고침: 캐시 갱신 요청');
          await disasterMessagesApi.refreshCache();
          console.log('✅ 캐시 갱신 완료');
        } catch (refreshError) {
          console.warn('⚠️ 캐시 갱신 실패, 기존 데이터로 진행:', refreshError);
        }
      }

      // 🔥 저장소 기반 API 호출
      const result = await disasterMessagesApi.fetchDisasterMessages(apiParams);

      if (result.success) {
        console.log('API 응답 데이터 (저장소 기반):', result.data);

        // 🔥 날짜 필터 적용 결과 로깅
        if (searchParams.crtDt) {
          console.log(`📅 날짜 ${searchParams.crtDt} 필터 결과:`, result.data?.length || 0, '건');
        }

        // 🔥 캐시 정보 처리
        if (result.cacheInfo) {
          setCacheInfo(result.cacheInfo);
          setIsFromCache(result.cacheInfo.fromCache || false);
          console.log('캐시 정보:', result.cacheInfo);
        }

        // 응답 데이터 형식 확인 및 처리 (기존과 동일)
        let processedData;

        if (result.data && result.data.body) {
          if (Array.isArray(result.data.body)) {
            console.log('API 응답이 body 배열 구조입니다. 길이:', result.data.body.length);
            processedData = [...result.data.body];
          } else {
            console.log('API 응답의 body가 배열이 아닙니다. 단일 객체를 배열로 변환합니다.');
            processedData = [result.data.body];
          }
        } else if (Array.isArray(result.data)) {
          console.log('API 응답이 직접 배열 형태입니다. 길이:', result.data.length);
          processedData = [...result.data];
        } else if (result.data && result.data.items && result.data.items.item) {
          // 공공데이터 포털 표준 응답 형식
          const items = result.data.items.item;
          if (Array.isArray(items)) {
            console.log('공공데이터 포털 응답의 items.item이 배열입니다. 길이:', items.length);
            processedData = [...items];
          } else {
            console.log('공공데이터 포털 응답의 items.item이 객체입니다. 배열로 변환합니다.');
            processedData = [items];
          }
        } else {
          console.log('API 응답이 객체 형태입니다. 원본 그대로 사용합니다.');
          processedData = result.data;
        }

        // 메시지 데이터 저장 (원본 형태 유지)
        setDisasterMessages(result.data);

        // 🔥 저장소 기반에서는 서버에서 이미 필터링되어 오므로 추가 필터링 불필요
        // 하지만 클라이언트에서 추가 필터링이 필요한 경우를 위해 유지
        const filtered = disasterMessagesApi.filterDisasterMessages(result.data, searchParams.filters);
        console.log('최종 표시할 데이터:', filtered);
        console.log('최종 데이터 길이:', filtered.length);

        // 🔥 날짜 필터 적용 디버깅
        if (searchParams.crtDt) {
          console.log(`📅 날짜 ${searchParams.crtDt} 최종 결과: ${filtered.length}건`);
        }

        setFilteredMessages(filtered);
        setLastUpdated(new Date());
      } else {
        console.error('API 오류:', result.error);
        setError(result.error || '데이터를 불러올 수 없습니다.');
        setDisasterMessages([]);
        setFilteredMessages([]);
        setCacheInfo(null);
        setIsFromCache(false);
      }
    } catch (err) {
      console.error('재난문자 데이터 로딩 오류:', err);
      setError('재난문자 정보를 불러오는 데 실패했습니다: ' + (err.message || '알 수 없는 오류'));
      setDisasterMessages([]);
      setFilteredMessages([]);
      setCacheInfo(null);
      setIsFromCache(false);
    } finally {
      setLoading(false);
    }
  };

  // 검색 파라미터 변경 핸들러
  const handleSearchParamsChange = (newParams) => {
    setSearchParams(prev => ({
      ...prev,
      ...newParams
    }));
  };

  // 필터 변경 핸들러
  const handleFilterChange = (newFilters) => {
    setSearchParams(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...newFilters
      }
    }));

    // 🔥 저장소 기반에서는 새로운 필터로 API 재호출
    // 이미 불러온 데이터에 필터 적용하는 대신 서버에서 필터링된 데이터 요청
    setTimeout(() => {
      fetchDisasterMessages();
    }, 100);
  };

  // 검색 실행 핸들러
  const handleSearch = () => {
    fetchDisasterMessages();
  };

  // 필터 초기화 핸들러
  const handleResetFilters = () => {
    setSearchParams(prev => ({
      ...prev,
      filters: {
        keyword: '',
        region: '',
        type: '',
        agency: '',
        missingPersonOnly: false
      }
    }));

    // 필터 초기화 후 데이터 재조회
    setTimeout(() => {
      fetchDisasterMessages();
    }, 100);
  };

  // 초기화 핸들러 (모든 파라미터 초기화)
  const handleReset = () => {
    setSearchParams({
      pageNo: 1,
      numOfRows: 20,
      crtDt: getTodayDate(),
      rgnNm: '',
      filters: {
        keyword: '',
        region: '',
        type: '',
        agency: '',
        missingPersonOnly: false
      }
    });

    setTimeout(() => {
      fetchDisasterMessages();
    }, 100);
  };

  // 🔥 새로 추가: 강제 새로고침 핸들러 (캐시 갱신 포함)
  const handleForceRefresh = () => {
    fetchDisasterMessages(true); // forceRefresh = true
  };

  // 🔥 새로 추가: 캐시 상태 조회 함수
  const fetchCacheStatus = async () => {
    try {
      const status = await disasterMessagesApi.getCacheStatus();
      if (status.success) {
        setCacheInfo(status.status);
        console.log('캐시 상태 업데이트:', status.status);
      }
    } catch (error) {
      console.error('캐시 상태 조회 실패:', error);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    console.log('컴포넌트 마운트, 데이터 로드 시작');
    fetchDisasterMessages();
    fetchCacheStatus();

    // 🔥 변경: 자동 갱신 간격 증가 (저장소 기반이므로)
    // 스케줄러가 2분마다 돌고 있으므로 클라이언트는 5분마다 체크
    const interval = setInterval(() => {
      console.log('재난문자 데이터 자동 갱신 (저장소 기반)');
      fetchDisasterMessages();
    }, 5 * 60 * 1000); // 5분

    return () => clearInterval(interval);
  }, []);

  // 새로고침 핸들러 (일반)
  const handleRefresh = () => {
    fetchDisasterMessages();
  };

  // 디버깅: 데이터 상태 출력
  console.log('현재 상태:', {
    disasterMessages: Array.isArray(disasterMessages)
      ? disasterMessages.length
      : (disasterMessages && disasterMessages.body
        ? (Array.isArray(disasterMessages.body)
          ? disasterMessages.body.length
          : '단일 객체')
        : 'object'),
    filteredMessages: Array.isArray(filteredMessages) ? filteredMessages.length : 'object',
    loading,
    error,
    isFromCache,
    cacheInfo,
    sampleMessage: Array.isArray(filteredMessages) && filteredMessages.length > 0
      ? { ...filteredMessages[0] }
      : null
  });

  return (
    <div className="disaster-messages-page">
      <div className="page-header">
        <h1 className="page-title">재난문자 알림</h1>
        {/* 🔥 새로 추가: 캐시 상태 표시 */}
        {cacheInfo && (
          <div className="cache-status-info">
            {isFromCache ? (
              <span className="cache-badge cache-hit">
                📦 저장소 데이터 • 마지막 업데이트: {cacheInfo.lastUpdateKR || '알 수 없음'}
              </span>
            ) : (
              <span className="cache-badge cache-miss">
                🌐 실시간 데이터
              </span>
            )}
          </div>
        )}
      </div>

      <DisasterMessagesSearch
        searchParams={searchParams}
        onSearchParamsChange={handleSearchParamsChange}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        onResetFilters={handleResetFilters}
        onReset={handleReset}
      />

      <div className="controls-bar">
        <div className="refresh-buttons">
          <button onClick={handleRefresh} className="refresh-button" disabled={loading}>
            {loading ? '로딩 중...' : '새로고침'}
          </button>

          {/* 🔥 새로 추가: 강제 새로고침 버튼 */}
          <button
            onClick={handleForceRefresh}
            className="force-refresh-button"
            disabled={loading}
            title="스케줄러를 통해 최신 데이터를 가져옵니다"
          >
            {loading ? '업데이트 중...' : '🔄 강제 업데이트'}
          </button>
        </div>

        <div className="info-text">
          {filteredMessages.length > 0 ? (
            <>
              총 {Array.isArray(disasterMessages) ? disasterMessages.length :
                (disasterMessages && disasterMessages.body ? disasterMessages.body.length : 0)}건 중
              {filteredMessages.length}건 표시 •
              최신순 정렬 •
              {/* 🔥 캐시 정보에 따른 업데이트 정보 표시 */}
              {isFromCache ? '저장소 기반 (2분마다 자동 업데이트)' : '실시간 데이터'}
            </>
          ) : loading ? (
            '데이터 로딩 중...'
          ) : (
            '데이터가 없습니다'
          )}
        </div>

        {lastUpdated && (
          <div className="last-updated-text">
            페이지 업데이트: {lastUpdated.toLocaleTimeString()}
            {/* 🔥 캐시 정보 추가 표시 */}
            {cacheInfo && cacheInfo.lastUpdateKR && (
              <span className="cache-update-info">
                • 데이터 업데이트: {cacheInfo.lastUpdateKR}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 🔥 새로 추가: 캐시 상태 상세 정보 (선택적 표시) */}
      {cacheInfo && cacheInfo.cacheStatus && (
        <div className="cache-details" style={{ fontSize: '0.8rem', color: '#666', marginBottom: '10px' }}>
          저장소 상태: {cacheInfo.cacheStatus.totalMessages}건 저장됨
          ({cacheInfo.cacheStatus.cacheUsage}% 사용)
          {cacheInfo.message && ` • ${cacheInfo.message}`}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <DisasterMessagesList
        messages={filteredMessages}
        loading={loading}
        error={error}
      />
    </div>
  );
};

export default DisasterMessagesPage;